import { Component, createSignal, JSX, onMount, Show } from "solid-js";
import { readFile } from "../lib/asyncFileReader";
import { decodeREPOSave, repoSaveSchema } from "../lib/repoSaveLib";
import * as v from 'valibot';
import type * as _ from 'wicg-file-system-access'

//TODO: this is a schema because I plan to load this data from an external source in the future
const fileStateSchema = v.object({
    fileMeta: v.object({
        name: v.string(),
        // milliseconds since epoch
        lastModified: v.number()
    }),
    data: repoSaveSchema
})

export type UploadState = {
    state: v.InferOutput<typeof fileStateSchema>
    handle?: FileSystemFileHandle
}

type SaveUploadProps = {
    onUpload: (state: UploadState) => void
}

const SaveUpload: Component<SaveUploadProps> = (props) => {
    const [beingDraggedOver, setBeingDraggedOver] = createSignal(false)
    const [error, setError] = createSignal<string | undefined>()
    const [loading, setLoading] = createSignal(false)
    const [loadedName, setLoadedName] = createSignal<string | undefined>()
    let labelRef!: HTMLLabelElement

    const loadFile = async (file: File, handle?: FileSystemFileHandle) => {
        const contents = await readFile(file)
        const saveData = await decodeREPOSave(new Uint8Array(contents))

        setLoadedName(file.name)
        props.onUpload({
            state: {
                fileMeta: {
                    name: file.name,
                    lastModified: file.lastModified
                },
                data: saveData
            },
            handle: handle
        })
    }

    const onDrop: JSX.EventHandler<HTMLLabelElement, DragEvent> = async (e) => {
        e.preventDefault()
        setBeingDraggedOver(false)
        if(e.dataTransfer === null) return
        if(loading()) return

        if(e.dataTransfer.items.length < 1) {
            setError('Error: Too few items')
            return
        }
        setError(undefined)
        const item = e.dataTransfer.items[0]

        try {
            setLoading(true)
            if(item.getAsFileSystemHandle !== undefined) {
                const handle = await item.getAsFileSystemHandle()
                if(handle === null) {
                    const file = item.getAsFile()
                    if(file === null) throw new Error('No file')
                    await loadFile(file)
                } else {
                    if(handle.kind === 'file') {
                        const fileHandle = handle as FileSystemFileHandle
                        await loadFile(await fileHandle.getFile(), fileHandle)
                    } else {
                        throw new Error('Directory selected')
                    }
                }
            } else {
                const file = item.getAsFile()
                if(file === null) throw new Error('No file')
                await loadFile(file)
            }
        } catch(e: any) {
            setError(e.toString())
        } finally {
            setLoading(false)
        }
    }

    const onFileChange: JSX.EventHandler<HTMLInputElement, Event> = async (e) => {
        if(loading()) return
        const files = e.currentTarget.files
        if(files === null || files.length === 0) return
        const file = files[0]
        setLoading(true)
        setError(undefined)
        try {
            await loadFile(file)
        } catch(e: any) {
            setError(e.toString())
        } finally {
            setLoading(false)
        }
    }

    onMount(() => {
        if(!('showOpenFilePicker' in window)) return

        labelRef.addEventListener('click', async e => {
            e.preventDefault()
            if(loading()) return
            try {
                setError(undefined)
                const [handle] = await window.showOpenFilePicker({
                    id: 'repo-save',
                    types: [
                        {
                            description: 'R.E.P.O. Save File (.es3)',
                            accept: {
                                'application/octet-stream': ['.es3']
                            }
                        }
                    ],
                    multiple: false
                })
                setLoading(true)
                await loadFile(await handle.getFile(), handle)
            } catch(e: any) {
                setError(e.toString())
            } finally {
                setLoading(false)
            }
        })
    })

    return (
        <div>
            <label
                class="block cursor-pointer border-2 border-dashed p-8"
                classList={{
                    'border-green-400': beingDraggedOver() && !loading()
                }}
                onDrop={onDrop}
                onDragOver={e => e.preventDefault()}
                onDragEnter={_ => setBeingDraggedOver(true)}
                onDragLeave={_ => setBeingDraggedOver(false)}
                ref={labelRef}
            >
                <Show when={loading()} fallback={<div>
                    Drag or click to load {loadedName() === undefined ? '' : 'a different '}save file
                    <Show when={loadedName()}>
                        {(name) => <p class="font-light">(Currently editing {name()})</p>}
                    </Show>
                </div>}>
                    <div class="loading"/>
                </Show>
                <input
                    type="file"
                    class="hidden"
                    disabled={loading()}
                    onChange={onFileChange}
                />
            </label>
            <Show when={error()}>
                {(error) => <pre class="alert alert-error text-wrap mt-2">
                    {error()}
                </pre>}
            </Show>
        </div>
    )
}

export default SaveUpload